/**
 * SymbolContext — ONE chosen symbol for the pages that read the market.
 *
 * Dashboard, Journal and Research were each pinned to '1HZ25V'. The system is
 * multi-asset: the operator picks the symbol, and every page reads the same one.
 *
 *   source of the list   candle-constructor GET /svc/candles/api/v2/assets
 *                        (the same list ProChart's picker uses)
 *   source of the choice ?symbol= in the URL (shareable) > localStorage
 *                        'aurexis_symbol' (shared with ProChart) > first asset
 *
 * Choosing writes both, so the chart and these pages stay on the same symbol.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CANDLE_CONSTRUCTOR_URL } from '@/utils/constants';

export interface AssetInfo { symbol: string; display_name: string; timeframes?: string[] }

interface SymbolCtx {
  symbol: string;
  setSymbol: (s: string) => void;
  assets: AssetInfo[];
  /** 'live' = list came from candle-constructor; 'fallback' = it did not answer. */
  assetsSource: 'loading' | 'live' | 'fallback';
}

export const DEFAULT_SYMBOL = '1HZ25V';
const STORAGE_KEY = 'aurexis_symbol';

function readStored(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

const Ctx = createContext<SymbolCtx | null>(null);

export function SymbolProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const urlSymbol = params.get('symbol');
  const [stored, setStored] = useState<string>(() => readStored() || DEFAULT_SYMBOL);
  const [assets, setAssets] = useState<AssetInfo[]>([{ symbol: DEFAULT_SYMBOL, display_name: DEFAULT_SYMBOL }]);
  const [assetsSource, setAssetsSource] = useState<SymbolCtx['assetsSource']>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${CANDLE_CONSTRUCTOR_URL}/api/v2/assets`, { signal: AbortSignal.timeout(8000) });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        const list: any[] = Array.isArray(d) ? d : d?.assets ?? d?.data ?? [];
        const clean: AssetInfo[] = list
          .filter((a) => a && (typeof a === 'string' || a.symbol))
          .map((a) => typeof a === 'string'
            ? { symbol: a, display_name: a }
            : { symbol: String(a.symbol), display_name: String(a.display_name || a.symbol), timeframes: a.timeframes });
        if (cancelled) return;
        if (clean.length) { setAssets(clean); setAssetsSource('live'); } else setAssetsSource('fallback');
      } catch {
        if (!cancelled) setAssetsSource('fallback');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Another tab (or the chart in another tab) changed the symbol.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY && e.newValue) setStored(e.newValue); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const symbol = urlSymbol || stored || DEFAULT_SYMBOL;

  const setSymbol = useCallback((s: string) => {
    if (!s) return;
    setStored(s);
    try { localStorage.setItem(STORAGE_KEY, s); } catch { /* private mode */ }
    const next = new URLSearchParams(params);
    next.set('symbol', s);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const value = useMemo(() => ({ symbol, setSymbol, assets, assetsSource }), [symbol, setSymbol, assets, assetsSource]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The chosen symbol. Outside a provider it falls back to the stored one (never throws). */
export function useSymbol(): SymbolCtx {
  const c = useContext(Ctx);
  if (c) return c;
  const s = readStored() || DEFAULT_SYMBOL;
  return { symbol: s, setSymbol: () => {}, assets: [{ symbol: s, display_name: s }], assetsSource: 'fallback' };
}

/** A compact picker. Lists every asset candle-constructor serves. */
export function SymbolPicker({ compact = false }: { compact?: boolean }) {
  const { symbol, setSymbol, assets, assetsSource } = useSymbol();
  const options = assets.some((a) => a.symbol === symbol) ? assets : [{ symbol, display_name: symbol }, ...assets];
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}
           title={assetsSource === 'fallback' ? 'candle-constructor did not answer: only the current symbol is listed' : 'Symbol'}>
      {!compact && <span>Symbol</span>}
      <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
              style={{ background: '#0c1220', color: '#e2e8f0', border: '1px solid #1e293b', borderRadius: 6, padding: '3px 6px', fontSize: 12 }}>
        {options.map((a) => <option key={a.symbol} value={a.symbol}>{a.display_name}</option>)}
      </select>
      {assetsSource === 'fallback' && <span style={{ color: '#f59e0b', fontSize: 10 }}>list offline</span>}
    </label>
  );
}
